import React from 'react';
import { University } from '../../../types';

interface OverviewTabProps {
  university: University;
  onTabChange: (tabId: string) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ university, onTabChange }) => {
  // Format numbers with commas
  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString();
  };

  // Format currency
  const formatCurrency = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return `$${num.toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric'
    });
  };

  return (
    <div className="tab-content" style={{ width: '100%', maxWidth: 'none', margin: 0, padding: 0 }}>
      <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">University Overview</h3>
      
      {/* Top Stats Grid */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="bg-blue-50/50 dark:bg-blue-900/20 rounded-xl p-6">
          <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Location</h4>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{university.location}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{university.environment}</p>
        </div>
        <div className="bg-green-50/50 dark:bg-green-900/20 rounded-xl p-6">
          <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Students</h4>
          <p className="text-3xl font-bold text-green-600 dark:text-green-400">{formatNumber(university.undergrad_students)}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">undergraduate students</p>
        </div>
      </div>

      {/* About Section */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">About {university.name}</h4>
        <div className="space-y-4 text-gray-600 dark:text-gray-400 leading-relaxed">
          <p>
            {university.name} is a prestigious research university located in {university.location}. 
            The campus provides an excellent {university.environment.toLowerCase()} environment for students 
            to pursue their academic goals.
          </p>
          <p>
            With {formatNumber(university.undergrad_students)} undergraduate students and a 
            {university.acceptance_rate ? ` ${university.acceptance_rate}%` : 'n excellent'} acceptance rate, 
            {university.name} offers a competitive yet supportive academic environment for students from diverse backgrounds.
          </p>
        </div>
      </div>

      {/* Bottom Stats Grid */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-100/60 dark:bg-gray-800/60 rounded-xl p-6 flex flex-col items-center justify-center">
          <div className="text-orange-500 dark:text-orange-400 text-3xl mb-3">
            <i className="fas fa-calendar"></i>
          </div>
          <h5 className="font-semibold text-gray-800 dark:text-gray-200">Application Deadline</h5>
          <p className="text-gray-600 dark:text-gray-400 mb-3">{formatDate(university.application_deadline)}</p>
          <button 
            onClick={() => onTabChange('requirements')}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white text-xs px-3 py-2 rounded-lg font-medium transition-colors"
          >
            Application Requirements
          </button>
        </div>
        <div className="bg-gray-100/60 dark:bg-gray-800/60 rounded-xl p-6 flex flex-col items-center justify-center">
          <div className="text-red-500 dark:text-red-400 text-3xl mb-3">
            <i className="fas fa-chart-bar"></i>
          </div>
          <h5 className="font-semibold text-gray-800 dark:text-gray-200">Acceptance Rate</h5>
          <p className="text-gray-600 dark:text-gray-400 mb-3">{university.acceptance_rate}%</p>
          <button 
            onClick={() => onTabChange('statistics')}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white text-xs px-3 py-2 rounded-lg font-medium transition-colors"
          >
            See How I Compare
          </button>
        </div>
        <div className="bg-gray-100/60 dark:bg-gray-800/60 rounded-xl p-6 flex flex-col items-center justify-center">
          <div className="text-green-500 dark:text-green-400 text-3xl mb-3">
            <i className="fas fa-dollar-sign"></i>
          </div>
          <h5 className="font-semibold text-gray-800 dark:text-gray-200">Net Price Per Year</h5>
          <p className="text-gray-600 dark:text-gray-400 mb-3">{formatCurrency(university.net_price_per_year)}</p>
          <button 
            onClick={() => onTabChange('tuition')}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white text-xs px-3 py-2 rounded-lg font-medium transition-colors"
          >
            Calculate Tuition
          </button>
        </div>
      </div>

      {/* Why We Suggested Section */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
        <div className="flex items-start">
          <div className="text-blue-500 dark:text-blue-400 text-xl mr-3 mt-1">
            <i className="fas fa-lightbulb"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-900 dark:text-blue-300 mb-3">Why did we suggest this school for you?</h4>
            <ul className="space-y-2 text-blue-800 dark:text-blue-300">
              <li className="flex items-start">
                <span className="text-blue-500 dark:text-blue-400 mr-2 mt-1">1)</span>
                <span>{university.name} matches your request for a prestigious school in a {university.environment.toLowerCase()} environment.</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 dark:text-blue-400 mr-2 mt-1">2)</span>
                <span>{university.name} has multiple essays that you can write from existing material.</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 dark:text-blue-400 mr-2 mt-1">3)</span>
                <span>We've detected special opportunities that suggest {university.name} may be actively recruiting applicants this year.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
